'use client';

import { useState, useEffect } from 'react';
import { ExclamationTriangleIcon, EyeIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import DataTable from '../components/DataTable';
import LoadingSpinner from '../components/LoadingSpinner';
import DebtPreviewModal from '../components/DebtPreviewModal';
import { toast } from 'sonner';
import { format } from 'date-fns';

const Debts = () => {
	const [debts, setDebts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [actionLoading, setActionLoading] = useState({});
	const [selectedDebt, setSelectedDebt] = useState(null);

	useEffect(() => {
		fetchDebts();
	}, []);

	const fetchDebts = async () => {
		try {
			setLoading(true);
			const response = await apiService.getDebts();

			if (response.data.status === 200) {
				setDebts(response.data.data.debts || []);
			} else {
				throw new Error(
					response.data.message || 'Failed to fetch debts',
				);
			}
		} catch (error) {
			const message =
				error.response?.data?.message ||
				error.message ||
				'Failed to fetch debts';
			toast.error(message);
		} finally {
			setLoading(false);
		}
	};

	const handleMarkAsDefault = async (debtId) => {
		if (
			!window.confirm(
				'Are you sure you want to mark this debt as defaulted?',
			)
		) {
			return;
		}

		setActionLoading((prev) => ({ ...prev, [debtId]: true }));

		try {
			const response = await apiService.markDebtAsDefault(debtId);

			if (response.data.status === 200) {
				toast.success('Debt marked as defaulted successfully');
				fetchDebts(); // Refresh the list
			} else {
				throw new Error(
					response.data.message || 'Failed to mark debt as defaulted',
				);
			}
		} catch (error) {
			const message =
				error.response?.data?.message ||
				error.message ||
				'Failed to mark debt as defaulted';
			toast.error(message);
		} finally {
			setActionLoading((prev) => ({ ...prev, [debtId]: false }));
		}
	};

	const getStatusBadge = (status) => {
		const statusClasses = {
			PENDING: 'status-pending',
			PARTIALLY_PAID: 'status-partially-paid',
			PAID: 'status-paid',
			DEFAULTED: 'status-defaulted',
			WRITTEN_OFF: 'bg-gray-100 text-gray-800',
		};

		return (
			<span
				className={`status-badge ${
					statusClasses[status] || 'bg-gray-100 text-gray-800'
				}`}>
				{status}
			</span>
		);
	};

	const columns = [
		{
			header: 'Amount',
			accessor: 'amount',
			render: (debt) => `₦${debt.amount.toLocaleString()}`,
		},
		{
			header: 'Due Date',
			accessor: 'dueDate',
			render: (debt) => format(new Date(debt.dueDate), 'MMM dd, yyyy'),
		},
		{
			header: 'Status',
			accessor: 'status',
			render: (debt) => getStatusBadge(debt.status),
		},
		{
			header: 'Priority',
			accessor: 'priority',
			render: (debt) => (
				<span
					className={`status-badge ${
						debt.priority === 1
							? 'priority-high'
							: debt.priority === 2
							? 'priority-medium'
							: 'priority-low'
					}`}>
					{debt.priority === 1
						? 'High'
						: debt.priority === 2
						? 'Medium'
						: 'Low'}
				</span>
			),
		},
		{
			header: 'Created',
			accessor: 'createdAt',
			render: (debt) => format(new Date(debt.createdAt), 'MMM dd, yyyy'),
		},
		{
			header: 'Actions',
			accessor: 'actions',
			render: (debt) => (
				<div className='flex items-center space-x-2'>
					<button
						onClick={() => setSelectedDebt(debt)}
						className='text-primary-600 hover:text-primary-900 transition-colors'
						title='Preview debt details'>
						<EyeIcon className='h-4 w-4' />
					</button>

					{debt.status !== 'DEFAULTED' && debt.status !== 'PAID' && (
						<button
							onClick={() => handleMarkAsDefault(debt.id)}
							disabled={actionLoading[debt.id]}
							className='text-red-600 hover:text-red-900 disabled:opacity-50 transition-colors'
							title='Mark as defaulted'>
							{actionLoading[debt.id] ? (
								<LoadingSpinner size='sm' />
							) : (
								<ExclamationTriangleIcon className='h-4 w-4' />
							)}
						</button>
					)}
				</div>
			),
		},
	];

	return (
		<div className='space-y-6'>
			{/* Stats cards */}
			<div className='grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4'>
				<div className='card'>
					<div className='flex items-center'>
						<div className='flex-shrink-0'>
							<div className='w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center'>
								<span className='text-primary-600 font-semibold text-sm'>
									{debts.length}
								</span>
							</div>
						</div>
						<div className='ml-3'>
							<p className='text-sm font-medium text-gray-500'>
								Total Debts
							</p>
						</div>
					</div>
				</div>

				<div className='card'>
					<div className='flex items-center'>
						<div className='flex-shrink-0'>
							<div className='w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center'>
								<span className='text-yellow-600 font-semibold text-sm'>
									{
										debts.filter(
											(d) => d.status === 'PENDING',
										).length
									}
								</span>
							</div>
						</div>
						<div className='ml-3'>
							<p className='text-sm font-medium text-gray-500'>
								Pending
							</p>
						</div>
					</div>
				</div>

				<div className='card'>
					<div className='flex items-center'>
						<div className='flex-shrink-0'>
							<div className='w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center'>
								<span className='text-green-600 font-semibold text-sm'>
									{
										debts.filter((d) => d.status === 'PAID')
											.length
									}
								</span>
							</div>
						</div>
						<div className='ml-3'>
							<p className='text-sm font-medium text-gray-500'>
								Paid
							</p>
						</div>
					</div>
				</div>

				<div className='card'>
					<div className='flex items-center'>
						<div className='flex-shrink-0'>
							<div className='w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center'>
								<span className='text-red-600 font-semibold text-sm'>
									{
										debts.filter(
											(d) => d.status === 'DEFAULTED',
										).length
									}
								</span>
							</div>
						</div>
						<div className='ml-3'>
							<p className='text-sm font-medium text-gray-500'>
								Defaulted
							</p>
						</div>
					</div>
				</div>
			</div>

			{/* Debts table */}
			<DataTable
				columns={columns}
				data={debts}
				loading={loading}
				emptyMessage='No debts found. Create your first debt record.'
			/>
			{/* Debt Preview Modal */}
			{selectedDebt && (
				<DebtPreviewModal
					debt={selectedDebt}
					closeModal={() => setSelectedDebt(null)}
				/>
			)}
		</div>
	);
};

export default Debts;
